import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Search } from 'lucide-react';
import { patients, users } from '../services/api';

const PatientStaffAssignment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patient, setPatient] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStaff, setSelectedStaff] = useState({
    primaryDoctor: '',
    primaryTherapist: '',
    secondaryTherapists: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch patient details
        const patientResponse = await patients.getById(id);
        setPatient(patientResponse.data);
        
        // Fetch staff list (therapists and doctors)
        const usersResponse = await users.getAll();
        const staffMembers = usersResponse.data.filter(user => 
          ['therapist', 'doctor'].includes(user.role)
        );
        setStaffList(staffMembers);

        // Set initial selections
        setSelectedStaff({
          primaryDoctor: patientResponse.data.primaryDoctor || '',
          primaryTherapist: patientResponse.data.primaryTherapist || '',
          secondaryTherapists: patientResponse.data.secondaryTherapists || []
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await patients.update(id, selectedStaff);
      navigate(`/dashboard/patients/${id}`);
    } catch (error) {
      console.error('Error updating patient staff:', error);
      setError('Failed to update staff assignments');
    }
  };

  const filteredStaff = staffList.filter(staff => {
    const fullName = `${staff.firstName} ${staff.lastName}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase()) ||
           staff.email.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-red-600">{error}</div>
        <button
          onClick={() => navigate('/dashboard/patients')}
          className="mt-4 text-brand-600 hover:text-brand-900"
        >
          Return to Patients List
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate(`/dashboard/patients/${id}`)}
            className="mr-4 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Assign Staff</h1>
            <p className="text-gray-600">
              Assign healthcare providers to {patient.firstName} {patient.lastName}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search staff members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Primary Doctor</label>
              <select
                value={selectedStaff.primaryDoctor}
                onChange={(e) => setSelectedStaff(prev => ({
                  ...prev,
                  primaryDoctor: e.target.value
                }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
              >
                <option value="">Select Primary Doctor</option>
                {filteredStaff
                  .filter(staff => staff.role === 'doctor')
                  .map(doctor => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.firstName} {doctor.lastName}
                    </option>
                  ))
                }
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Primary Therapist</label>
              <select
                value={selectedStaff.primaryTherapist}
                onChange={(e) => setSelectedStaff(prev => ({
                  ...prev,
                  primaryTherapist: e.target.value
                }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
              >
                <option value="">Select Primary Therapist</option>
                {filteredStaff
                  .filter(staff => staff.role === 'therapist')
                  .map(therapist => (
                    <option key={therapist.id} value={therapist.id}>
                      {therapist.firstName} {therapist.lastName}
                    </option>
                  ))
                }
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Therapists</label>
              <select
                multiple
                value={selectedStaff.secondaryTherapists}
                onChange={(e) => setSelectedStaff(prev => ({
                  ...prev,
                  secondaryTherapists: Array.from(e.target.selectedOptions, option => option.value)
                }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                size={5}
              >
                {filteredStaff
                  .filter(staff => staff.role === 'therapist')
                  .map(therapist => (
                    <option key={therapist.id} value={therapist.id}>
                      {therapist.firstName} {therapist.lastName}
                    </option>
                  ))
                }
              </select>
              <p className="mt-1 text-sm text-gray-500">Hold Ctrl/Cmd to select multiple therapists</p>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate(`/dashboard/patients/${id}`)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-brand-700 text-white rounded-md text-sm font-medium hover:bg-brand-800 flex items-center"
            >
              <Save size={16} className="mr-2" />
              Save Assignments
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PatientStaffAssignment; 